import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';

interface Job {
  id: number;
  title: string;
  description: string;
  job_type: string;
  location_type: string;
  location: string;
  salary: string;
  company: string;
  employer_name: string;
  created_at: string;
}

const JobList: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [filters, setFilters] = useState({
    jobType: 'all',
    location: 'all',
    search: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [jobs, filters]);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/api/jobs');
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = jobs;

    if (filters.jobType !== 'all') {
      filtered = filtered.filter(job => job.job_type === filters.jobType);
    }

    if (filters.location !== 'all') {
      filtered = filtered.filter(job => job.location && job.location.toLowerCase().includes(filters.location.toLowerCase()));
    }

    if (filters.search) {
      filtered = filtered.filter(job => 
        job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        job.description.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const getUniqueLocations = () => {
    const locations = jobs.map(job => job.location).filter(loc => loc);
    return Array.from(new Set(locations));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return <div className="loading">Loading jobs...</div>;
  }

  return (
    <div className="job-list-container">
      <h1>Job Listings</h1>
      
      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            name="search"
            placeholder="Search jobs..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            name="jobType"
            value={filters.jobType}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="all">All Job Types</option>
            <option value="full-time">Full Time</option>
            <option value="part-time">Part Time</option>
            <option value="contract">Contract</option>
            <option value="internship">Internship</option>
            <option value="freelance">Freelance</option>
          </select>
        </div>
        
        <div className="filter-group">
          <select
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="all">All Locations</option>
            {getUniqueLocations().map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="jobs-grid">
        {filteredJobs.length === 0 ? (
          <div className="no-jobs">
            <p>No jobs found matching your criteria.</p>
          </div>
        ) : (
          filteredJobs.map(job => (
            <div key={job.id} className="job-card">
              <div className="job-header">
                <h3>{job.title}</h3>
                <span className={`job-type ${job.job_type}`}>
                  {job.job_type.replace('-', ' ')}
                </span>
              </div>
              
              <div className="job-company">
                <strong>{job.company}</strong> - {job.employer_name}
              </div>
              
              <div className="job-description">
                {job.description.substring(0, 200)}...
              </div>
              
              <div className="job-meta">
                <span className={`location-type ${job.location_type}`}>
                  {job.location_type}
                </span>
                {job.location && <span className="location">{job.location}</span>}
                {job.salary && <span className="salary">{job.salary}</span>}
                <span className="date">Posted {formatDate(job.created_at)}</span>
              </div>
              
              <div className="job-actions">
                <Link to={`/jobs/${job.id}`} className="btn btn-primary">
                  View Details
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default JobList;
